import React, { useCallback, useEffect, useState } from "react";
import { Check, ArrowRight, Upload, X, Loader2 } from "lucide-react";
import { apiGetPublic, apiPostPublic, apiUploadPublic } from "../../Api/Client.js";
import { CulturePoints } from "./CultureCheckScreen.jsx";
import onboardingVideo from "../../Onboarding.mp4";
import "../../Styles/CultureCheck.css";

// The whole joining sequence, for somebody who has no account yet.
//
//   culture -> confidentiality -> offer -> your details -> identity -> waiting
//
// The SERVER decides which step is open; this page only renders whichever one
// it is told. That matters because the order is a promise about what a person
// had seen before they agreed to the next thing, and a browser is not a place
// to keep that promise.

// Who the agreement is with comes from the offer's company, never from this file.
function ndaEntity(company) {
  const name = (company?.name || "").trim() || "the company";
  const legal = (company?.legal_name || "").trim();
  return legal ? `${name} (${legal})` : name;
}

const ndaText = (company) => [
  ["Confidential information", `Technical and business information belonging to ${ndaEntity(company)} — ideas, products, software, research, costs, finances, customers, marketing and business plans, whether or not marked confidential.`],
  ["What you agree to", "To use that information only for your work here, to share it inside the company only with people who need it, and never to pass it outside without written permission."],
  ["How long it lasts", "For the whole of your engagement, and for five years after it ends, whatever the reason it ends."],
  ["Data privacy", "To follow the company's data protection rules for any personal data you touch, and to report any breach the moment you notice one."],
  ["Professional conduct", "To deal with the company's money, materials and documents honestly, and not to act in any way that breaks the confidence placed in you."],
];

// The details step. Names match the server's DETAIL_FIELDS (joining.py); the
// server re-validates everything, this only saves a round trip.
const DETAIL_INPUTS = [
  { name: "full_name", label: "Full name", required: true, autoComplete: "name", max: 180 },
  { name: "phone", label: "Phone number", required: true, type: "tel", autoComplete: "tel", max: 40 },
  { name: "city", label: "City", required: true, autoComplete: "address-level2", max: 120 },
  { name: "address", label: "Current address", required: true, autoComplete: "street-address", max: 500, wide: true },
  { name: "emergency_contact", label: "Emergency contact", required: true, max: 150,
    hint: "Name and phone number of someone we can call.", wide: true },
  { name: "college_name", label: "College", max: 220 },
  { name: "year_of_graduation", label: "Year of graduation", inputMode: "numeric", max: 4 },
  { name: "github_username", label: "GitHub username", max: 39 },
];

function checkDetails(values) {
  const errors = {};
  for (const f of DETAIL_INPUTS) {
    if (f.required && !(values[f.name] || "").trim()) errors[f.name] = "This is required.";
  }
  const phone = (values.phone || "").trim();
  const digits = phone.replace(/\D/g, "");
  if (phone && (!/^\+?[0-9][0-9 ()-]*$/.test(phone) || digits.length < 7 || digits.length > 15)) {
    errors.phone = "Enter a valid phone number.";
  }
  const year = (values.year_of_graduation || "").trim();
  const maxYear = new Date().getFullYear() + 10;
  if (year && (!/^\d{4}$/.test(year) || Number(year) < 1950 || Number(year) > maxYear)) {
    errors.year_of_graduation = `Enter a year between 1950 and ${maxYear}.`;
  }
  const gh = (values.github_username || "").trim();
  if (gh && !/^[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?$/.test(gh)) {
    errors.github_username = "Letters, numbers and single hyphens only.";
  }
  return errors;
}

function StepHeader({ kicker, title, children }) {
  return (
    <header className="Cc-Head">
      <span className="Cc-Kicker">{kicker}</span>
      <h1>{title}</h1>
      {children}
    </header>
  );
}

export function JoiningScreen() {
  const token = React.useMemo(() => {
    const m = window.location.pathname.match(/\/join\/([^/?#]+)/);
    return m ? m[1] : "";
  }, []);

  const [state, setState] = useState(null);
  const [phase, setPhase] = useState(token ? "loading" : "invalid");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Per-step local state
  const [readPoints, setReadPoints] = useState({});
  const [ndaAgreed, setNdaAgreed] = useState(false);
  const [offerAgreed, setOfferAgreed] = useState(false);
  const [file, setFile] = useState(null);
  const [details, setDetails] = useState(null);
  const [detailErrors, setDetailErrors] = useState({});
  const [detailsChecked, setDetailsChecked] = useState(false);

  // Prefill once, when the server opens the details step.
  useEffect(() => {
    if (state?.step === "details" && state.details && details === null) setDetails(state.details);
  }, [state, details]);

  // "invalid" means the server said this link is no good. A dropped connection or a server fault says
  // nothing about the link, so it gets its own "error" screen with a retry.
  const load = useCallback(() => {
    setPhase("loading");
    apiGetPublic(`/MainApp/joining/${token}/state`)
      .then((r) => {
        if (!r || !r.valid) { setPhase("invalid"); return; }
        setState(r);
        setPhase("ready");
      })
      .catch(() => setPhase("error"));
  }, [token]);

  useEffect(() => { if (token) load(); }, [token, load]);

  const advance = async (step) => {
    setBusy(true); setError("");
    try {
      const r = await apiPostPublic(`/MainApp/joining/${token}/step/${step}`, {});
      if (r && r.valid) { setState(r); window.scrollTo(0, 0); }
      else setError((r && r.detail) || "That did not go through. Please try again.");
    } catch (e) {
      setError(e?.message || "That did not go through. Please try again.");
    } finally { setBusy(false); }
  };

  const sendDetails = async () => {
    const local = checkDetails(details || {});
    setDetailErrors(local);
    if (Object.keys(local).length) { setError("Please check the highlighted fields."); return; }
    setBusy(true); setError("");
    try {
      // The email is shown for checking only; the server ignores it anyway.
      const { email: _shownOnly, ...editable } = details;
      const r = await apiPostPublic(`/MainApp/joining/${token}/step/details`, editable);
      if (r && r.valid) { setState(r); setDetailErrors({}); window.scrollTo(0, 0); }
      else {
        setDetailErrors((r && r.errors) || {});
        setError((r && r.detail) || "That did not go through. Please try again.");
      }
    } catch (e) {
      setError(e?.message || "That did not go through. Please try again.");
    } finally { setBusy(false); }
  };

  const sendDocument = async () => {
    if (!file) return;
    setBusy(true); setError("");
    try {
      const body = new FormData();
      body.append("file", file);
      const r = await apiUploadPublic(`/MainApp/joining/${token}/document`, body);
      if (r && r.valid) { setState(r); window.scrollTo(0, 0); }
      else setError((r && r.detail) || "The upload did not go through.");
    } catch (e) {
      setError(e?.message || "The upload did not go through.");
    } finally { setBusy(false); }
  };

  if (phase === "loading") return <section className="Cc-Wrap"><p className="Cc-Plain">Loading…</p></section>;
  if (phase === "error") {
    return (
      <section className="Cc-Wrap">
        <h1 className="Cc-Plain-Head">We could not load this page.</h1>
        <p className="Cc-Plain">Check your connection and try again. Your link is fine.</p>
        <button className="Cc-Btn" onClick={load}>Try again</button>
      </section>
    );
  }
  if (phase === "invalid") {
    return (
      <section className="Cc-Wrap">
        <h1 className="Cc-Plain-Head">This link is not valid.</h1>
        <p className="Cc-Plain">It may have expired or already been used. Please contact whoever sent you the offer.</p>
      </section>
    );
  }

  const step = state.step;
  const first = state.first_name || "";

  return (
    <section className="Cc-Wrap">
      {error && <div className="Cc-Error">{error}</div>}

      {/* ── 1. culture ─────────────────────────────────────────────────── */}
      {step === "culture" && (
        <CulturePoints
          track={state.track === "sales" ? "sales" : "eng"}
          seniority={state.seniority === "full" ? "full" : "junior"}
          firstName={first}
          checked={readPoints}
          onToggle={(id) => setReadPoints((d) => ({ ...d, [id]: !d[id] }))}
          busy={busy}
          onContinue={() => advance("culture")}
        />
      )}

      {/* ── 2. confidentiality ─────────────────────────────────────────── */}
      {step === "nda" && (
        <>
          <StepHeader kicker="Step two of five" title="Your NDA — confidentiality, before anything else is shared.">
            <p>
              This is your Non-Disclosure Agreement. You are about to see the terms of your offer, and
              after that you will see how this business works from the inside. Both need this in place
              first. It is short, and it is the version we hold too — you will get a copy by email the
              moment you accept.
            </p>
          </StepHeader>
          <ol className="Cc-List">
            {ndaText(state.company).map(([head, body]) => (
              <li key={head} className="Cc-Item">
                <div className="Cc-Title" style={{ marginBottom: 6 }}>{head}</div>
                <p className="Cc-Plain" style={{ fontSize: 14.5 }}>{body}</p>
              </li>
            ))}
          </ol>
          <div className="Cc-Uploads">
            <button className="Cc-Check" role="checkbox" aria-checked={ndaAgreed} onClick={() => setNdaAgreed((v) => !v)}>
              <span className="Cc-Box">{ndaAgreed && <Check size={14} strokeWidth={3} />}</span>
              <span className="Cc-Title">
                I, {state.candidate_name}, have read this agreement and agree to be bound by it.
              </span>
            </button>
          </div>
          <Foot
            hint={ndaAgreed ? "Your offer is next." : "Tick the box to carry on."}
            disabled={!ndaAgreed || busy}
            busy={busy}
            onContinue={() => advance("nda")}
            label="Agree and continue"
          />
        </>
      )}

      {/* ── 3. the offer ───────────────────────────────────────────────── */}
      {step === "offer" && (
        <>
          <StepHeader kicker="Step three of five" title={`${first}, here is your offer.`}>
            <p>
              This is the letter in full. Read it, including the section on notice and leaving, which is
              where most questions come up later. Accepting sends you a signed copy by email.
            </p>
          </StepHeader>
          <div className="Cc-Frame">
            <iframe
              sandbox=""
              title="Your offer letter"
              src={`/api/MainApp/offer/preview/${token}`}
              style={{ width: "100%", height: "72vh", border: "none", background: "#fff" }}
            />
          </div>
          <div className="Cc-Uploads">
            <button className="Cc-Check" role="checkbox" aria-checked={offerAgreed} onClick={() => setOfferAgreed((v) => !v)}>
              <span className="Cc-Box">{offerAgreed && <Check size={14} strokeWidth={3} />}</span>
              <span className="Cc-Title">
                I, {state.candidate_name}, accept this offer and its terms.
              </span>
            </button>
          </div>
          <Foot
            hint={offerAgreed ? "Two short steps left." : "Tick the box to accept."}
            disabled={!offerAgreed || busy}
            busy={busy}
            onContinue={() => advance("offer")}
            label="Accept the offer"
          />
        </>
      )}

      {/* ── 4. your details ────────────────────────────────────────────── */}
      {step === "details" && details && (
        <>
          <StepHeader kicker="Step four of five" title="Check your details.">
            <p>
              Your account is set up with these. Your name is filled in from the offer — correct it if
              anything is wrong.
            </p>
          </StepHeader>
          <div className="Jn-Form">
            <div className="Jn-Field is-wide">
              <span className="Jn-Label">Email</span>
              <div className="Jn-Readonly" id="jn-email">{details.email}</div>
              <span className="Jn-Msg">
                Your login details go here. Wrong address? Contact the person who sent your offer. They can
                correct it and send you a new link.
              </span>
            </div>
            {DETAIL_INPUTS.map((f) => {
              const err = detailErrors[f.name];
              const id = `jn-${f.name}`;
              return (
                <label key={f.name} htmlFor={id} className={`Jn-Field${f.wide ? " is-wide" : ""}${err ? " has-error" : ""}`}>
                  <span className="Jn-Label">{f.label}{!f.required && <em> (optional)</em>}</span>
                  <input
                    id={id}
                    type={f.type || "text"}
                    inputMode={f.inputMode}
                    autoComplete={f.autoComplete || "off"}
                    maxLength={f.max}
                    value={details[f.name] || ""}
                    aria-invalid={err ? true : undefined}
                    aria-describedby={err || f.hint ? `${id}-msg` : undefined}
                    onChange={(e) => {
                      const v = e.target.value;
                      setDetails((d) => ({ ...d, [f.name]: v }));
                      if (err) setDetailErrors((m) => { const n = { ...m }; delete n[f.name]; return n; });
                    }}
                  />
                  {(err || f.hint) && <span id={`${id}-msg`} className="Jn-Msg">{err || f.hint}</span>}
                </label>
              );
            })}
          </div>
          <div className="Cc-Uploads">
            <button className="Cc-Check" role="checkbox" aria-checked={detailsChecked} onClick={() => setDetailsChecked((v) => !v)}>
              <span className="Cc-Box">{detailsChecked && <Check size={14} strokeWidth={3} />}</span>
              <span className="Cc-Title">These details are correct.</span>
            </button>
          </div>
          <Foot
            hint={detailsChecked ? "One short step left." : "Tick the box once you have checked them."}
            disabled={!detailsChecked || busy}
            busy={busy}
            onContinue={sendDetails}
            label="Save and continue"
          />
        </>
      )}

      {/* ── 5. identity ────────────────────────────────────────────────── */}
      {step === "identity" && (
        <>
          <StepHeader kicker="Step five of five" title="One document, and you are done.">
            <p>
              A government ID — Aadhaar, PAN, passport, driving licence or voter ID. A clear photo is fine.
              It goes to the person who offered you the role and is never visible to the wider team.
            </p>
          </StepHeader>
          {state.document?.status === "Rejected" && (
            <div className="Cc-Error" style={{ marginBottom: 14 }}>
              {state.document.note || "The last copy could not be read clearly enough."} Please send another.
            </div>
          )}
          <div className={`Cc-Upload${file ? " is-set" : ""}`}>
            <div className="Cc-Upload-Text">
              <strong>Government ID</strong>
              <span>{file ? `${file.name} · ${Math.max(1, Math.round(file.size / 1024))} KB` : "PDF or photo, under 8 MB"}</span>
            </div>
            {file ? (
              <button className="Cc-Upload-Clear" onClick={() => setFile(null)} aria-label="Remove file"><X size={15} /></button>
            ) : (
              <label className="Cc-Upload-Btn">
                <Upload size={14} /> Choose file
                <input type="file" accept=".pdf,.png,.jpg,.jpeg,.heic,.webp"
                       onChange={(e) => setFile(e.target.files && e.target.files[0])} />
              </label>
            )}
          </div>
          <Foot
            hint={file ? "We will confirm by email." : "Choose a file to finish."}
            disabled={!file || busy}
            busy={busy}
            onContinue={sendDocument}
            label="Send and finish"
          />
        </>
      )}

      {/* ── 6. waiting, with something worth reading ───────────────────── */}
      {(step === "review" || step === "done") && (
        <>
          <StepHeader
            kicker={step === "done" ? "You are in" : "Almost there"}
            title={step === "done"
              ? `Welcome aboard, ${first}.`
              : `Thank you, ${first}. Your ID is being checked.`}
          >
            <p>
              {step === "done"
                ? "Your account has been created and your login details are in your inbox."
                : "It goes to the person who offered you the role. You will get an email either way — confirming your account, or asking for a clearer copy. Nothing more is needed from you."}
            </p>
          </StepHeader>

          <div className="Cc-Uploads">
            <h2>While you wait — how this place actually works</h2>
            <p className="Cc-Uploads-Lede">
              Worth twenty minutes before your first day. This is the same onboarding video every new
              hire watches — how work gets tracked, what the culture expects, and where things get
              communicated.
            </p>
            <div className="Cc-Frame">
              <video
                src={onboardingVideo}
                controls
                controlsList="nodownload"
                style={{ width: "100%", display: "block" }}
              />
            </div>
            <p className="Cc-Plain" style={{ marginTop: 12, fontSize: 13.5 }}>
              Read next: <a href="https://banao.tech" target="_blank" rel="noopener noreferrer">Banao Technologies</a>,{" "}
              <a href="https://interviewgod.ai" target="_blank" rel="noopener noreferrer">InterviewGod</a>,{" "}
              <a href="https://vidya.online" target="_blank" rel="noopener noreferrer">Vidya</a>,{" "}
              <a href="https://vikaas.ai" target="_blank" rel="noopener noreferrer">Vikaas</a>,{" "}
              <a href="https://axonhq.ai" target="_blank" rel="noopener noreferrer">Axon</a>.
            </p>
          </div>
        </>
      )}
    </section>
  );
}

function Foot({ hint, disabled, busy, onContinue, label }) {
  return (
    <div className="Cc-Foot">
      <div className="Cc-Foot-Row">
        <span className="Cc-Count">{hint}</span>
        <div className="Cc-Actions">
          <button className="Cc-Btn" disabled={disabled} onClick={onContinue}>
            {busy ? <Loader2 size={15} className="Cc-Spin" /> : null}
            {label} {!busy && <ArrowRight size={15} />}
          </button>
        </div>
      </div>
    </div>
  );
}
